import { ScrollType, useNextScrollTo } from 'hooks/useNextScrollTo'
import { useRouter } from 'hooks/useRouter'
import { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Icons } from 'svgs'
import Box from 'UIKit/Box/Box'
import { TOP_LOADER_CONSTANTS } from './constant'

interface TopLoaderProps {
  color?: string
  showSpinner?: boolean
}

const isSamePath = (url1: string, url2: string): boolean => {
  const pathname1 = url1?.split('?')[0]
  const pathname2 = url2?.split('?')[0]
  return pathname1 === pathname2
}

const TopLoader = ({
  color = TOP_LOADER_CONSTANTS.DEFAULT_COLOR,
  showSpinner = TOP_LOADER_CONSTANTS.DEFAULT_SHOW_SPINNER,
}: TopLoaderProps) => {
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isCompleteRef = useRef(false)
  const currentUrlRef = useRef<string>('')
  const isProgressRunningRef = useRef(false)

  const router = useRouter()
  const { scroll } = useNextScrollTo()

  const startRef = useRef(() => {
    setIsVisible(true)
    setProgress(0)
    isCompleteRef.current = false
    isProgressRunningRef.current = true
    clearTimeout(timeoutRef.current as NodeJS.Timeout)
    clearTimeout(resetTimeoutRef.current as NodeJS.Timeout)

    const simulateProgress = () => {
      setProgress((oldProgress) => {
        if (isCompleteRef.current) return oldProgress

        const diff = (1 - Math.random()) * TOP_LOADER_CONSTANTS.PROGRESS_INCREMENT
        const newProgress = Math.min(oldProgress + diff, TOP_LOADER_CONSTANTS.MAX_PROGRESS_BEFORE_COMPLETE)

        if (newProgress < TOP_LOADER_CONSTANTS.MAX_PROGRESS_BEFORE_COMPLETE) {
          timeoutRef.current = setTimeout(simulateProgress, TOP_LOADER_CONSTANTS.PROGRESS_DELAY)
        }

        return newProgress
      })
    }

    timeoutRef.current = setTimeout(simulateProgress, TOP_LOADER_CONSTANTS.START_DELAY)
  })

  const completeRef = useRef(() => {
    if (!isProgressRunningRef.current) {
      return
    }

    clearTimeout(timeoutRef.current as NodeJS.Timeout)
    clearTimeout(resetTimeoutRef.current as NodeJS.Timeout)

    isCompleteRef.current = true
    setProgress(TOP_LOADER_CONSTANTS.MAX_PROGRESS)

    const reset = () => {
      if (!isCompleteRef.current) return

      setIsVisible(false)
      setProgress(0)
      isCompleteRef.current = false
      isProgressRunningRef.current = false
    }

    resetTimeoutRef.current = setTimeout(reset, TOP_LOADER_CONSTANTS.RESET_DELAY)
  })

  useEffect(() => {
    currentUrlRef.current = router.asPath

    const start = (url: string) => {
      if (isSamePath(url, currentUrlRef.current)) {
        return
      }

      currentUrlRef.current = url
      startRef.current()
    }

    const complete = (url: string, options: any) => {
      if (!options?.shallow) {
        scroll({ type: ScrollType.ToTop })
      }

      completeRef.current()
    }

    const error = () => {
      if (isProgressRunningRef.current) {
        clearTimeout(timeoutRef.current as NodeJS.Timeout)
        clearTimeout(resetTimeoutRef.current as NodeJS.Timeout)

        isCompleteRef.current = true
        setProgress(TOP_LOADER_CONSTANTS.MAX_PROGRESS)

        const reset = () => {
          setIsVisible(false)
          setProgress(0)
          isCompleteRef.current = false
          isProgressRunningRef.current = false
        }

        resetTimeoutRef.current = setTimeout(reset, TOP_LOADER_CONSTANTS.RESET_DELAY)
      }
    }

    router.events.on('routeChangeStart', start)
    router.events.on('routeChangeComplete', complete)
    router.events.on('routeChangeError', error)

    return () => {
      router.events.off('routeChangeStart', start)
      router.events.off('routeChangeComplete', complete)
      router.events.off('routeChangeError', error)
    }
  }, [router])

  if (!isVisible) return null

  return (
    <>
      <StyledProgressBar
        style={{
          background: color,
          width: `${progress}%`,
          opacity: isVisible ? 1 : 0,
        }}
      />
      {showSpinner && (
        <StyledSpinner>
          <StyledSpinnerIcon style={{ borderTopColor: color }} />
        </StyledSpinner>
      )}
    </>
  )
}

const progressAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const StyledProgressBar = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  transition: width 300ms ease-out, opacity 150ms ease-in;
  transform: translate3d(0, 0, 0);
  height: ${TOP_LOADER_CONSTANTS.DEFAULT_HEIGHT_MOBILE}px;

  ${({ theme }) => theme.mediaQueries.sm} {
    height: ${TOP_LOADER_CONSTANTS.DEFAULT_HEIGHT}px;
  }
`

const StyledSpinner = styled(Box)`
  position: fixed;
  top: 15px;
  right: 15px;
  z-index: 9999;
`

const StyledSpinnerIcon = styled(Icons.CircleLoadingBackgroundIcon)`
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-radius: 50%;
  animation: ${progressAnimation} 0.7s linear infinite;
`

export default TopLoader
